<template>
  <div id="role-form">
    <h3 class="mt-2">
      {{ this.entity.id ? "Edit" : "Create" }} {{ $t("systemRoleForm.systemRole") }} {{ entity.name }}
    </h3>
    <BButtonToolbar class="mb-2 pt-2 pb-2 border-light border-top border-bottom">
      <BButtonGroup>
        <BButton size="sm" variant="outline-primary" @click="onClose" class="custom-button">{{
          $t("systemRoleForm.close")
        }}</BButton>
        <BButton
          :disabled="!isDirty"
          class="ml-2"
          size="sm"
          variant="primary"
          @click="onSave"
          style="background-color: #0054a6; color: white"
        >
          <font-awesome-icon icon="save"></font-awesome-icon>
          {{ $t("systemRoleForm.save") }}
        </BButton>
      </BButtonGroup>
    </BButtonToolbar>

    <div class="row">
      <div class="col-8">
        <BForm ref="form">
          <BFormGroup id="input-group-1" description="Name should be unique." label="Name:" label-for="input-1">
            <BFormInput id="input-1" v-model="entity.name" placeholder="Enter Name" required type="text"></BFormInput>
          </BFormGroup>
        </BForm>
      </div>
      <!-- /.col-8 -->
      <div class="col-4">
        <PermissionsPanel
          :disabled="isCreateMode || !canModifyPermissions"
          provider-name="R"
          :provider-key="entity.name"
          v-if="formReady"
        />
      </div>
      <!-- /.col-4 -->
    </div>
    <!-- /.row -->
  </div>
</template>

<script lang="ts">
import { Inject } from "inversify-props";
import { RoleApi } from "@/api";
import { IdentityRoleCreateDto, IdentityRoleDto, IdentityRoleUpdateDto } from "@/api/model";
import BaseFormMixin from "@/core/views/forms/BaseForm";
import { AxiosResponse } from "axios";
import AuditFormBlock from "@/core/views/pages/_components/AuditFormBlock.vue";
import PermissionsPanel from "@/core/views/pages/SaaS/_components/PermissionsPanel.vue";
import { PermissionGuard } from "@/core/plugins/permissions";
import { mixins, Options } from "vue-class-component";
import { BButton, BButtonToolbar, BButtonGroup, BForm, BFormGroup, BFormInput } from "bootstrap-vue-next";

@Options({
  components: { PermissionsPanel, AuditFormBlock, BButton, BButtonGroup, BForm, BFormInput, BButtonToolbar, BFormGroup }
})
export default class SystemRoleForm extends mixins(
  BaseFormMixin<IdentityRoleDto, IdentityRoleCreateDto, IdentityRoleUpdateDto>
) {
  meta = {
    gridRoute: "SystemRolesGrid",
    formEditRoute: "SystemRoleFormEdit",
    entityTypeName: "System Role"
  };
  @Inject("RoleApi")
  private readonly rolesApi!: RoleApi;

  get canModifyPermissions(): boolean {
    return PermissionGuard("AbpIdentity.Roles.ManagePermissions");
  }

  getRequest(id: string): Promise<AxiosResponse<IdentityRoleDto>> {
    return this.rolesApi.apiIdentityRolesIdGet(id);
  }

  createRequest(entity: IdentityRoleDto): Promise<AxiosResponse<IdentityRoleDto>> {
    return this.rolesApi.apiIdentityRolesPost(this.mapToCreateDto(entity));
  }

  updateRequest(id: string, entity: IdentityRoleDto): Promise<AxiosResponse<IdentityRoleDto>> {
    return this.rolesApi.apiIdentityRolesIdPut(id, this.mapToUpdateDto(entity));
  }

  mapToCreateUpdateDto(entity: IdentityRoleDto): IdentityRoleDto {
    return entity;
  }

  mapToCreateDto(entity: IdentityRoleDto): IdentityRoleCreateDto {
    return {
      name: entity.name,
      isDefault: false,
      isPublic: true
    } as IdentityRoleCreateDto;
  }

  mapToUpdateDto(entity: IdentityRoleDto): IdentityRoleUpdateDto {
    return {
      name: entity.name,
      isDefault: entity.isDefault,
      isPublic: entity.isPublic,
      concurrencyStamp: entity.concurrencyStamp
    } as IdentityRoleUpdateDto;
  }
}
</script>
<style scoped>
.custom-button {
  color: #0054a6;
  background-color: white;
  border-color: #0054a6;
  transition: all 0.02s ease;
}
.custom-button:hover {
  color: white !important;
  background-color: #0054a6 !important;
  border-color: #0054a6;
}
#role-form {
  font-size: 14px;
  font-family: Arial, sans-serif;
}
</style>
